import React, { useState, useEffect, useContext, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek, isSameDay, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, PlusIcon, ClockIcon, UserIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import EventModal from './EventModal';
import EditEventModal from './EditEventModal';
import UpcomingEvents from './widgets/UpcomingEvents';
import DogCockpit from './widgets/DogCockpit';
import AddCalendarEvent from './AddCalendarEvent';

const Calendar: React.FC = () => {
  const { user } = useContext(AuthContext);
  const isBoard = hasAdminRights(user);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<any>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState<any[]>([]);
  const [highlightedCreatedDays, setHighlightedCreatedDays] = useState(new Set<string>());
  const [highlightedBookedDays, setHighlightedBookedDays] = useState(new Set<string>());
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Funktion zum Neuladen der Events
  const refreshCalendar = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/calendar');
      const data = response.data;
      if (data.success) {
        const formattedEvents = data.events.reduce((acc: any, event: any) => {
          // Parse the date from the ISO string
          const startDate = new Date(event.start);
          const dateStr = format(startDate, 'yyyy-MM-dd');
          if (!acc[dateStr]) acc[dateStr] = [];
          
          // Create start and end Date objects using the parsed date and time strings
          const [startHour, startMinute] = event.start_time.split(':');
          const [endHour, endMinute] = event.end_time.split(':');
          
          const startDateTime = new Date(startDate);
          startDateTime.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);
          
          const endDateTime = new Date(startDate);
          endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
          
          acc[dateStr].push({
            id: event.id,
            title: event.title,
            start: startDateTime,
            end: endDateTime,
            extendedProps: {
              color: event.color,
              bookable: parseInt(event.bookable) || 0,
              bookingCount: parseInt(event.booking_count) || 0,
              creator: {
                name: event.creator?.name || event.creator?.id?.toString() || 'Unbekannt',
                id: event.creator?.id,
                email: event.creator?.email,
                image: event.creator?.image,
              },
              cotrainer: event.cotrainer ? {
                name: event.cotrainer.name || event.cotrainer.id?.toString() || 'Unbekannt',
                id: event.cotrainer.id,
                email: event.cotrainer.email,
                image: event.cotrainer.image,
              } : null,
              repeat: event.repeat,
              repeat_end: event.repeat_end,
              unique_id: event.unique_id,
              taetigkeitsbericht: event.taetigkeitsbericht || 0,
              taetigkeitsbericht_text: event.taetigkeitsbericht_text || '',
              buchen_bis: event.buchen_bis || null,
              isVeranstaltung: event.is_veranstaltung || false,
            },
          });
          return acc;
        }, {});
        setEvents(formattedEvents);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Kalendereinträge:', err);
      }
    }
  }, []);

  const fetchUserEvents = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/calendar/user-events-calendar/unknown');
      const data = response.data;
      if (data.eventsCreated) {
        const createdDates = new Set<string>(data.eventsCreated.map((event: any) => event.start));
        setHighlightedCreatedDays(createdDates);
      }
      if (data.eventsBooked) {
        const bookedDates = new Set<string>(data.eventsBooked.map((event: any) => event.start));
        setHighlightedBookedDays(bookedDates);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Benutzer-Events:', err);
      }
    }
  }, []);

  useEffect(() => {
    refreshCalendar();
    fetchUserEvents();
  }, [refreshCalendar, fetchUserEvents]);

  const hasEvent = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return events[dateStr] && events[dateStr].length > 0;
  };

  const getEventForDay = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return events[dateStr] || [];
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedDate(date);
    const eventsOnDate = events[dateStr] || [];
    setSelectedDateEvents(eventsOnDate);
    
    // On mobile: if only one event, select it directly
    const isMobile = window.innerWidth < 640;
    if (isMobile && eventsOnDate.length === 1) {
      setSelectedEvent(eventsOnDate[0]);
    } else {
      setSelectedEvent(null);
    }
  };

  const handleEventClick = (event: any) => {
    // Set both states together to avoid inconsistencies
    setSelectedEvent(event);
    setSelectedDateEvents((prevEvents) => {
      // Ensure selectedDateEvents contains at least this event
      if (!prevEvents.some(e => e.id === event.id)) {
        return [event];
      }
      return prevEvents;
    });
  };

  const closeModal = () => {
    // Reset all modal-related states together
    setSelectedEvent(null);
    setSelectedDateEvents([]);
    setSelectedDate(null);
  };

  const handleDeleteEvent = (deleteResponse: any) => {
    if (deleteResponse && deleteResponse.success && deleteResponse.event_ids) {
      const eventIdsToDelete = deleteResponse.event_ids.map(String);
      setEvents((prevEvents: any) => {
        const newEvents = { ...prevEvents };
        Object.keys(newEvents).forEach((date) => {
          newEvents[date] = newEvents[date].filter(
            (event: any) => !eventIdsToDelete.includes(String(event.id))
          );
          if (newEvents[date].length === 0) delete newEvents[date];
        });
        return newEvents;
      });
      setSelectedEvent(null);
      setSelectedDateEvents([]);
    }
  };

  const handleBookEvent = (id: number, bookingCount: number) => {
    setEvents((prevEvents: any) => {
      const newEvents = { ...prevEvents };
      Object.keys(newEvents).forEach((date) => {
        newEvents[date] = newEvents[date].map((event: any) =>
          event.id === id
            ? { ...event, extendedProps: { ...event.extendedProps, bookingCount } }
            : event
        );
      });
      return newEvents;
    });
  };

  const handleAddEvent = (newEvent: any) => {
    const dateStr = format(newEvent.start, 'yyyy-MM-dd');
    setEvents((prevEvents: any) => ({
      ...prevEvents,
      [dateStr]: [
        ...(prevEvents[dateStr] || []),
        {
          id: newEvent.id,
          title: newEvent.title,
          start: newEvent.start,
          end: newEvent.end,
          extendedProps: {
            color: newEvent.color,
            bookable: newEvent.bookable,
            bookingCount: newEvent.bookingCount,
            creator: {
              name: newEvent.creator,
              id: newEvent.creator_id,
            },
            cotrainer: newEvent.cotrainer || null,
            repeat: newEvent.repeat,
            repeat_end: newEvent.repeat_end,
            unique_id: newEvent.unique_id,
            taetigkeitsbericht: newEvent.taetigkeitsbericht || 0,
            taetigkeitsbericht_text: newEvent.taetigkeitsbericht_text || '',
          },
        },
      ],
    }));
    setHighlightedCreatedDays(prev => new Set([...Array.from(prev), dateStr]));
  };

  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [addEventDate, setAddEventDate] = useState<Date | null>(null);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 11) return 'Guten Morgen';
    if (hour >= 11 && hour < 18) return 'Hallo';
    if (hour >= 18 && hour < 22) return 'Guten Abend';
    return 'Hallo';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-3 sm:space-y-6">
      {/* Story-Hero Intro — kein Foto, nur Text */}
      <section className="pt-2 pb-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
          Mein Verein · {format(new Date(), 'EEEE, d. MMMM yyyy', { locale: de })}
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight">
          {getGreeting()}{user?.vorname ? <>, <span className="text-primary-600 dark:text-primary-400">{user.vorname}.</span></> : <span className="text-primary-600 dark:text-primary-400">.</span>}
        </h1>
      </section>

      {/* Cockpit-Dashboard: Hunde · Aufgaben · Nächstes Training · 7-Tage-Strip */}
      <DogCockpit />

      {/* Buchungen-Widget */}
      <div className="grid grid-cols-1">
        <UpcomingEvents />
      </div>

      {/* Kalender als Hero-Element */}
      <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-white dark:bg-[#1c2432] border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/20 rounded-xl flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white">
                {format(currentDate, 'MMMM yyyy', { locale: de })}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {/* Plus button for board members */}
              {isBoard && (
                <button
                  onClick={() => {
                    setAddEventDate(selectedDate);
                    setShowAddEventModal(true);
                  }}
                  className="p-2 sm:p-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-all duration-200"
                  title="Event hinzufügen"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={goToToday}
                className="hidden sm:block px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all duration-200 text-sm font-medium text-gray-900 dark:text-white"
              >
                Heute
              </button>
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden">
                <button
                  onClick={previousMonth}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-300"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-300"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="p-2 sm:p-4">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-2">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, index) => (
              <div 
                key={day} 
                className={`text-center text-xs sm:text-sm font-semibold py-2 ${
                  index >= 5 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <span className="truncate">{day}</span>
              </div>
            ))}
          </div>
          
          {/* Calendar Days Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map(day => {
              const dayEvents = getEventForDay(day);
              const dateStr = format(day, 'yyyy-MM-dd');
              const isCreated = highlightedCreatedDays.has(dateStr);
              const isBooked = highlightedBookedDays.has(dateStr) && !isCreated;
              const isWeekend = getDay(day) === 0 || getDay(day) === 6;
              const dayNumber = format(day, 'd');
              
              return (
                <div
                  key={day.toString()}
                  onClick={() => handleDateClick(day)}
                  className={`
                    relative cursor-pointer transition-all duration-200 rounded-lg
                    ${!isSameMonth(day, currentDate) ? 'opacity-40' : ''}
                    ${selectedDate && isSameDay(day, selectedDate) ? 'ring-2 ring-primary-500 ring-offset-1' : ''}
                    ${isToday(day) ? 'bg-primary-50 dark:bg-primary-900/10' : ''}
                    hover:bg-gray-50 dark:hover:bg-gray-800/50
                  `}
                >
                  {/* Mobile View */}
                  <div className="sm:hidden p-1">
                    <div className="flex flex-col items-center">
                      <div className={`
                        relative w-11 h-11 flex items-center justify-center rounded-full
                        ${isToday(day) ? 'bg-primary-600 text-white' : ''}
                        ${isWeekend && !isToday(day) ? 'text-primary-500 dark:text-primary-400' : ''}
                        ${!isWeekend && !isToday(day) ? 'text-gray-900 dark:text-gray-100' : ''}
                        ${isCreated ? 'text-primary-600 dark:text-primary-400' : ''}
                        ${isBooked && !isCreated ? 'text-success-600 dark:text-success-400' : ''}
                      `}>
                        <span className="text-sm font-medium">{dayNumber}</span>
                      </div>
                      {dayEvents.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {dayEvents.slice(0, 3).map((event: any, idx: number) => (
                            <div
                              key={idx}
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: event.extendedProps?.color || '#3B82F6' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Desktop View */}
                  <div className={`hidden sm:block p-2 min-h-[120px] md:min-h-[140px] lg:min-h-[160px] rounded-lg ${
                    isWeekend && isSameMonth(day, currentDate) ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''
                  }`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`
                        text-sm font-semibold inline-flex items-center justify-center transition-all duration-200
                        ${isToday(day) ? 'bg-primary-600 text-white w-7 h-7 rounded-full' : ''}
                        ${isWeekend && !isToday(day) ? 'text-primary-600 dark:text-primary-400' : ''}
                        ${!isWeekend && !isToday(day) ? 'text-gray-700 dark:text-gray-300' : ''}
                      `}>
                        {dayNumber}
                      </span>
                      {/* Indicator for booked/created */}
                      {(isBooked || isCreated) && (
                        <div className={`w-2 h-2 rounded-full ${isBooked ? 'bg-success-500' : 'bg-primary-500'}`} />
                      )}
                    </div>
                    
                    {/* Desktop Events */}
                    {dayEvents.length > 0 && (
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event: any, idx: number) => {
                          const bookable = event.extendedProps?.bookable || 0;
                          const bookingCount = event.extendedProps?.bookingCount || 0;
                          const hasBookings = bookable > 0;
                          const isFull = bookingCount >= bookable;
                          const isAlmostFull = bookingCount > bookable * 0.8;

                          return (
                            <div
                              key={idx}
                              className="group relative"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}
                            >
                              <div
                                className="text-xs px-2 py-1.5 rounded-lg text-white font-medium cursor-pointer
                                          transition-all duration-200"
                                style={{
                                  backgroundColor: event.extendedProps?.color || '#3B82F6',
                                }}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <div className="flex items-center gap-1 min-w-0 flex-1">
                                    <ClockIcon className="h-3 w-3 flex-shrink-0 opacity-80" />
                                    <span className="font-semibold">{format(event.start, 'HH:mm')}</span>
                                  </div>
                                  {hasBookings && (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${
                                      isFull
                                        ? 'bg-error-500 text-white'
                                        : isAlmostFull
                                        ? 'bg-warning-500 text-white'
                                        : 'bg-success-500 text-white'
                                    }`}>
                                      {bookingCount}/{bookable}
                                    </span>
                                  )}
                                </div>
                                <div className="truncate mt-0.5">{event.title}</div>
                              </div>
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 pl-1.5">
                            +{dayEvents.length - 3} weitere
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Agenda View - Android Style - Show when date is selected and user is board member */}
      {selectedDate && (getEventForDay(selectedDate).length > 1 || (isBoard && getEventForDay(selectedDate).length === 0)) && (
        <div className="sm:hidden bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            {format(selectedDate, 'EEEE, d. MMMM', { locale: de })}
          </h3>
          <div className="space-y-3">
            {getEventForDay(selectedDate).map((event: any) => (
              <div
                key={event.id}
                onClick={() => handleEventClick(event)}
                className="flex items-start gap-3 p-3 rounded-xl border-l-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                style={{ borderLeftColor: event.extendedProps?.color || '#3B82F6' }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{event.title}</h4>
                  {event.extendedProps?.creator && (
                    <div className="flex items-center gap-2 mt-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {event.extendedProps.creator.name}
                      </span>
                    </div>
                  )}
                  {event.extendedProps?.bookable > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      {event.extendedProps.bookingCount}/{event.extendedProps.bookable} Plätze belegt
                    </div>
                  )}
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event Modal */}
      {(selectedEvent || selectedDateEvents.length > 0) && (
        <EventModal
          event={selectedEvent}
          events={selectedDateEvents}
          onClose={closeModal}
          onDelete={handleDeleteEvent}
          onBook={handleBookEvent}
          refreshCalendar={refreshCalendar}
          onEventSelect={setSelectedEvent}
          onEdit={(event: any) => {
            setEditingEvent(event);
            setShowEditModal(true);
            closeModal();
          }}
        />
      )}
      
      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <EditEventModal
          event={editingEvent}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEvent(null);
          }}
          refreshCalendar={refreshCalendar}
        />
      )}
      
      {/* Add Event Modal */}
      {isBoard && (
        <AddCalendarEvent
          isOpen={showAddEventModal}
          onClose={() => {
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
          selectedDate={addEventDate}
          onAddEvent={(event) => {
            handleAddEvent(event);
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
          refreshCalendar={() => {
            refreshCalendar();
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
        />
      )}
    </div>
  );
};

export default Calendar;